<template>
  <div class="p-2 border-1 surface-border border-round">
    <!-- Header with current path and action buttons -->
    <div class="flex align-items-center justify-content-between mb-2">
      <div class="text-sm">
        <span class="text-600">Текущий путь:</span>
        <span class="font-medium ml-1">{{ internalPath }}</span>
      </div>
      <div class="flex align-items-center gap-2">
        <Button
          v-if="showAddButton"
          size="small"
          text
          icon="pi pi-plus"
          label="Добавить"
          @click="$emit('update:selectedPaths', [...selectedPaths, internalPath])"
          :disabled="disabled || selectedPaths.includes(internalPath)"
        />
        <Button
          size="small"
          text
          icon="pi pi-refresh"
          @click="reloadFolders"
          :loading="loadingFolders"
          :disabled="disabled"
        />
      </div>
    </div>

    <!-- Breadcrumb -->
    <Breadcrumb :model="breadcrumbItems" class="mb-2">
      <template #item="{ item, props }">
        <a href="#" @click.prevent="navigateBreadcrumb(item)" v-bind="props.action">
          <span class="text-primary font-semibold">{{ item.label }}</span>
        </a>
      </template>
    </Breadcrumb>

    <!-- Folder list -->
    <div class="folder-picker-list" :style="{ maxHeight: maxHeight, overflowY: 'auto' }">
      <!-- Go up -->
      <div
        v-if="internalPath !== '/'"
        class="p-2 border-round cursor-pointer hover:bg-primary-50 mb-1 flex align-items-center gap-2"
        @click="goUp"
      >
        <i class="pi pi-arrow-left"></i>
        <span class="text-sm">Вверх</span>
      </div>

      <!-- Loading -->
      <div v-if="loadingFolders" class="flex align-items-center gap-2 px-2 py-3 text-sm text-600">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Загружаем список папок…</span>
      </div>

      <!-- Folders -->
      <template v-else>
        <div
          v-for="folder in pickerFolders"
          :key="folder.path"
          :class="['folder-picker-row', { 'is-selected': showCheckboxes && selectedPaths.includes(folder.path) }]"
        >
          <div class="flex align-items-center gap-2 cursor-pointer flex-1" @click="openFolder(folder)">
            <i class="pi pi-folder text-orange-500"></i>
            <span class="text-sm font-medium">{{ folder.name }}</span>
            <span v-if="folder.itemCount != null" class="ml-auto text-xs text-500">{{ folder.itemCount }} элементов</span>
          </div>
          <Checkbox
            v-if="showCheckboxes"
            :modelValue="selectedPaths.includes(folder.path)"
            binary
            @update:modelValue="val => toggleSelection(folder.path, val)"
            @click.stop
          />
        </div>
        <div v-if="pickerFolders.length === 0" class="text-600 text-sm px-2 py-3">
          Подпапок нет
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Breadcrumb from 'primevue/breadcrumb'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import apiService from '../services/apiService'
import { makeBreadcrumbItems, getParentPath } from '../utils/dateHelpers'

const props = defineProps({
  path: {
    type: String,
    default: '/'
  },
  showCheckboxes: {
    type: Boolean,
    default: false
  },
  selectedPaths: {
    type: Array,
    default: () => []
  },
  maxHeight: {
    type: String,
    default: '228px'
  },
  showAddButton: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:path', 'update:selectedPaths'])

const internalPath = ref(props.path)
const pickerFolders = ref([])
const loadingFolders = ref(false)

const breadcrumbItems = computed(() => makeBreadcrumbItems(internalPath.value))

async function reloadFolders() {
  loadingFolders.value = true
  try {
    const response = await apiService.getImagesAndFolders(internalPath.value)
    pickerFolders.value = response.folders || []
  } catch {
    pickerFolders.value = []
  } finally {
    loadingFolders.value = false
  }
}

function navigateBreadcrumb(item) {
  if (item?.path != null) {
    internalPath.value = item.path
    emit('update:path', item.path)
    reloadFolders()
  }
}

function openFolder(folder) {
  internalPath.value = folder.path
  emit('update:path', folder.path)
  reloadFolders()
}

function goUp() {
  const newPath = getParentPath(internalPath.value)
  internalPath.value = newPath
  emit('update:path', newPath)
  reloadFolders()
}

function toggleSelection(folderPath, value) {
  let updated
  if (value) {
    updated = [...props.selectedPaths, folderPath]
  } else {
    updated = props.selectedPaths.filter(p => p !== folderPath)
  }
  emit('update:selectedPaths', updated)
}

// Sync internal path when prop changes externally
watch(() => props.path, (newPath) => {
  if (newPath !== internalPath.value) {
    internalPath.value = newPath
    reloadFolders()
  }
}, { immediate: true })
</script>

<style scoped>
.folder-picker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid transparent;
  transition: background 0.2s ease;
}
.folder-picker-row:hover {
  background: var(--surface-100);
}
.folder-picker-row.is-selected {
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
}
</style>
